import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import { groundApi } from '../services/api';
import { Ground } from '../models';

interface GroundAutocompleteProps {
  value: Ground | null;
  onChange: (value: Ground | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const GroundAutocomplete: React.FC<GroundAutocompleteProps> = ({
  value,
  onChange,
  error,
  helperText,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Ground[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Handle input change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch grounds when search term changes
  useEffect(() => {
    let active = true;

    const fetchGrounds = async () => {
      if (disabled) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await groundApi.getAll({
          search: searchTerm,
          page_size: 20,
        });
        if (active) {
          setOptions(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching grounds:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchGrounds();

    return () => {
      active = false;
    };
  }, [searchTerm, disabled]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      getOptionLabel={(option) => `${option.name}, ${option.city}`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disabled={disabled}
      size='small'
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {option.image && (
            <img
              loading="lazy"
              width="20"
              height="20"
              src={option.image}
              alt={option.name}
              style={{ marginRight: '10px', borderRadius: '4px' }}
            />
          )}
          {option.name}, {option.city}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Ground"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default GroundAutocomplete;
